import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { AD_BANNER } from "@/constants/testIds";
import {
  X, ChevronLeft, ChevronRight, MessageCircle, Instagram, Phone, Mail, Globe, Facebook, ExternalLink,
} from "lucide-react";

const KIND_META = {
  whatsapp: { icon: MessageCircle, label: "WhatsApp", bg: "bg-emerald-500 hover:bg-emerald-600", href: (v) => `https://wa.me/${v.replace(/\D/g, "")}` },
  instagram: { icon: Instagram, label: "Instagram", bg: "bg-gradient-to-r from-fuchsia-500 to-orange-500 hover:opacity-90", href: (v) => `https://instagram.com/${v.replace(/^@/, "")}` },
  facebook: { icon: Facebook, label: "Facebook", bg: "bg-blue-600 hover:bg-blue-700", href: (v) => (v.startsWith("http") ? v : `https://facebook.com/${v.replace(/^@/, "")}`) },
  phone: { icon: Phone, label: "Ligar", bg: "bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)]", href: (v) => `tel:${v.replace(/[^\d+]/g, "")}` },
  email: { icon: Mail, label: "E-mail", bg: "bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)]", href: (v) => `mailto:${v}` },
  url: { icon: Globe, label: "Site", bg: "bg-gray-800 hover:bg-gray-900", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
};

const Slide = ({ ad, onTrackClick, onDismiss }) => {
  const contacts = Array.isArray(ad.contacts) ? ad.contacts.filter((c) => KIND_META[c.kind] && c.value) : [];
  return (
    <div
      data-testid={AD_BANNER.root(ad.id)}
      className="relative w-full flex-shrink-0 bg-white rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(13,27,75,0.12)]"
    >
      <div className="absolute top-3 left-3 z-10">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-extrabold uppercase tracking-widest text-[var(--fi-red)] shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--fi-red)]" />
          Publicidade
        </span>
      </div>
      <button type="button" onClick={onDismiss} aria-label="Fechar anúncio" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur">
        <X className="w-4 h-4" />
      </button>
      <a
        href={ad.target_url || "#"}
        target={ad.target_url ? "_blank" : undefined}
        rel="noopener noreferrer"
        onClick={(e) => { if (!ad.target_url) e.preventDefault(); else onTrackClick(); }}
        className="block relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden bg-gray-100 group"
      >
        <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" draggable={false} />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute left-4 right-4 bottom-3 text-white">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/80">{ad.sponsor}</div>
          <div className="font-display text-2xl sm:text-3xl font-black uppercase leading-tight drop-shadow-md">{ad.title}</div>
        </div>
      </a>
      <div className="p-4 sm:p-5">
        {ad.description && <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{ad.description}</p>}
        {(contacts.length > 0 || ad.target_url) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {contacts.map((c, i) => {
              const M = KIND_META[c.kind];
              if (!M) return null;
              const Icon = M.icon;
              return (
                <a key={i} href={M.href(c.value)} target={c.kind === "email" || c.kind === "phone" ? undefined : "_blank"} rel="noopener noreferrer" onClick={onTrackClick} data-testid={`ad-cta-${ad.id}-${c.kind}`} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full ${M.bg} text-white text-xs font-bold uppercase tracking-widest transition-all shadow-sm hover:shadow-md`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span>{c.label || M.label}</span>
                </a>
              );
            })}
            {ad.target_url && (
              <a href={ad.target_url} target="_blank" rel="noopener noreferrer" onClick={onTrackClick} data-testid={`ad-cta-${ad.id}-more`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-gray-200 hover:border-[var(--fi-blue)] text-[var(--fi-blue)] text-xs font-bold uppercase tracking-widest transition-colors">
                <ExternalLink className="w-3.5 h-3.5" />
                Saiba mais
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AdBanner = ({ placement = "all", className = "" }) => {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    api.get("/ads/active", { params: { placement } })
      .then((r) => setAds(r.data))
      .catch(() => {});
  }, [placement]);

  const current = ads[idx];
  const durationMs = Math.max(3, Math.min(15, current?.duration_seconds || 6)) * 1000;

  // Auto-advance timer with progress
  useEffect(() => {
    if (ads.length < 2 || paused || !current) return;
    setProgress(0);
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const el = Date.now() - startedAt;
      const p = Math.min(100, (el / durationMs) * 100);
      setProgress(p);
    }, 100);
    const t = setTimeout(() => setIdx((i) => (i + 1) % ads.length), durationMs);
    return () => {
      clearTimeout(t);
      clearInterval(tick);
    };
  }, [idx, ads.length, paused, durationMs, current]);

  const go = (i) => setIdx(((i % ads.length) + ads.length) % ads.length);
  const prev = () => go(idx - 1);
  const next = () => go(idx + 1);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const trackClick = () => { if (current) api.post(`/ads/${current.id}/click`).catch(() => {}); };

  if (dismissed || ads.length === 0) return null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative ${className}`}
      data-testid="ad-carousel"
      aria-roledescription="carousel"
      aria-label="Publicidade"
    >
      {/* Slides track */}
      <div className="overflow-hidden rounded-2xl">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {ads.map((ad) => (
            <Slide
              key={ad.id}
              ad={ad}
              onTrackClick={trackClick}
              onDismiss={() => setDismissed(true)}
            />
          ))}
        </div>
      </div>

      {ads.length > 1 && (
        <>
          <button
            onClick={prev}
            data-testid="ad-carousel-prev"
            aria-label="Anúncio anterior"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur transition-colors z-20"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            data-testid="ad-carousel-next"
            aria-label="Próximo anúncio"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur transition-colors z-20"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Progress bar */}
          <div className="absolute left-0 right-0 top-0 h-1 bg-black/10 rounded-t-2xl overflow-hidden">
            <div
              className="h-full bg-[var(--fi-red)] transition-[width] duration-100 ease-linear"
              style={{ width: `${paused ? 0 : progress}%` }}
            />
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5 mt-3">
            {ads.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                data-testid={`ad-carousel-dot-${i}`}
                aria-label={`Ir para anúncio ${i + 1}`}
                aria-current={i === idx}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[var(--fi-red)]" : "w-1.5 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AdBanner;
